import React, { useContext, useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Avatar,
  FormLabel,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { Archive, Delete, Edit } from '@material-ui/icons';

import PropTypes from 'prop-types';

import CustomCheckBox from '../../fields/customCheckBox';
import DeleteToolBar from '../../fields/deleteToolBar';
import SearchField from '../../fields/searchField';
import TablePaginationActions from '../../fields/tablePaginationActions';
import TimedOutShedulesFilter from '../../fields/timedOutShedulesFilter';

import { filteredSearch, getComparator, getDataSheet, stableSort } from '../../../utils/helpers';

import { complementaryColor, defaultRowsPerPage, defaultSortOrder, rowsPerPageOptions, greenColor, amberColor, redColor } from '../../../utils/constants';

import translations from '../../../languages/i18n';

import fieldStyles from '../../../styles/fieldStyles';
import { customOptionsTableStyles } from '../../../styles/formElementsStyles';

import { SessionContext } from '../../../utils/context';

const BlueSwitch = withStyles({
  switchBase: {
    color: 'default',
    '&$checked': {
      color: complementaryColor,
    },
    '&$checked + $track': {
      backgroundColor: complementaryColor,
    },
  },
  checked: {},
  track: {},
})(Switch);

export default function CliniciansList({
  cliniciansData, clinicianEnrolments, handleViewClinician, handleViewClinicianSchedules, handleDelete,
  handleSelectedCliniciansData, isProgramAdministrator, selectedClinicians,
}) {
  const classesField = fieldStyles();
  const classesForm = customOptionsTableStyles();

  const sessionContext = useContext(SessionContext);

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [order, setOrder] = React.useState(defaultSortOrder); // Indica l'ordre per defecte
  const [orderBy, setOrderBy] = React.useState('username'); // Indica la columna per la que ordenam per defecte
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchString, setSearchString] = useState('');
  const [showOnlyMine, setShowOnlyMine] = useState(true); // Per defecte només mostram els clínics administats i/o supervisats pel clínic
  const [timedOut, setTimedOut] = useState(false);
  const [timedOutDays, setTimedOutDays] = useState(null);
  const timedOutDaysOptions = [2, 7, 10];

  const filterUserSchedulesStatus = (list, pTimedOut, pTimedOutDays) => {
    const out = [];
    let firtStep = null;
    list.map((item) => {
      if (pTimedOut === true) {
        // Agafam valors caducats
        const prevStep = item.userSchedulesStatus.filter((value) => value < 0);
        // Eliminam aquells que incumpleixen el valor límit caducitat establert
        if (pTimedOutDays !== null) {
          firtStep = prevStep.filter((value) => value >= -pTimedOutDays);
        } else {
          firtStep = [...prevStep];
        }
        // Ordenam - el més crític queda primer [-5,-1] o [1, 3]
        firtStep.sort((a, b) => a - b);
        if (firtStep.length > 0) {
          out.push({ ...item, form: firtStep[0] });
        }
      } else {
        // Agafam valors NO caducats
        firtStep = item.userSchedulesStatus.filter((value) => value >= 0);
        // Ordenam - el més crític queda primer [-5,-1] o [1, 3]
        firtStep.sort((a, b) => a - b);
        out.push({ ...item, form: firtStep[0] });
      }
    });

    // Si mostram els actius ordenam pacients - el més critic primer
    out.sort((a, b) => a.form - b.form);
    return out;
  };

  const onlyMyClinicians = () => {
    let cliniciansIdsAdministeredByClinician = [];
    if (isProgramAdministrator) { // En cas d'haver entrat com a clínic administrador, obtenim els ids dels clínics dels programes que administra
      // Extreim la informació relativa a l'usuari clínic
      const clinicianData = cliniciansData.find((clinician) => clinician._id === sessionContext.userId);
      // Obtenim els ids dels clínics dels programes administrats pel clínic
      const programsClinicians = clinicianData.programs.map((program) => {
        const isAdministered = program.accessibleBy.clinicians.some((clinician) => clinician.clinician !== null && clinician.admin === true && clinician.clinician._id === sessionContext.userId); // Si posam primer la condició clinician.admin === true ens estalviam comprovacions, ja que en la majoria de casos .admin serà false
        if (isAdministered) return program.accessibleBy.clinicians;
      });
      // Llevam els elements undefined (es corresponen als programes que no administra) de l'array
      const cliniciansIdsAdministeredByClinicianObjArray = programsClinicians.filter((clinician) => clinician !== undefined);
      // Agafam els ids dels clinics administrats. Alerta que pot contenir ids duplicats
      cliniciansIdsAdministeredByClinician = cliniciansIdsAdministeredByClinicianObjArray.flatMap((members) => members.map((clinician) => clinician.clinician._id));
    }
    // Tant si s'ha entrat com a clínic administrador o com supervisor, obtenim els ids dels clínics dels enrolments que supervisa
    // Ficam dins un array els equips dels enrolments que supervisa el clínic
    const teamsMembers = clinicianEnrolments.flatMap((enrolment) => {
      const isSupervised = enrolment.supervisedBy.some((clinician) => clinician.admin === true && clinician.clinician === sessionContext.userId); // Si posam primer la condició clinician.admin === true ens estalviam comprovacions, ja que en la majoria de casos .admin serà false
      if (isSupervised) return enrolment.supervisedBy;
    });
    // Llevam els elements undefined (es corresponen als enrolments que no supervisa) de l'array
    const cliniciansSupervisedByClinicianObjArray = teamsMembers.filter((clinician) => clinician !== undefined);
    // Agafam els ids dels clinics supervisats. Alerta que pot contenir ids duplicats
    const cliniciansIdsSupervisedByClinician = cliniciansSupervisedByClinicianObjArray.map((clinician) => clinician.clinician);

    // Unim els ids dels clínics administrats amb els supervisats i eliminam els duplicats
    const cliniciansIdsAdministeredAndSupervisedWithDuplicates = cliniciansIdsAdministeredByClinician.concat(cliniciansIdsSupervisedByClinician);
    const cliniciansIdsAdministeredAndSupervised = [...new Set(cliniciansIdsAdministeredAndSupervisedWithDuplicates)];

    // De tots els clínics, ens quedam amb els que tenen un id administat o supervisat
    const filteredClinicians = cliniciansData.filter((clinician) => cliniciansIdsAdministeredAndSupervised.some((clinicianId) => clinicianId === clinician._id));
    return filteredClinicians;
  };

  // Per a que faci l'await i esperi a acabar l'execució onlyMyClinicians() ho he de fer així:
  useEffect(() => {
    const initialOnlyMyClinicians = async () => {
      // per defecte no veim els caducats
      const filteredCliniciansData = filterUserSchedulesStatus(showOnlyMine ? await onlyMyClinicians() : cliniciansData, timedOut, timedOutDays);
      setFilteredUsers(filteredCliniciansData);
      setSearchString('');
    };
    // Per defecte, inicialment només mostram els clinics administrats i/o supervisats pel clinic
    initialOnlyMyClinicians();
  }, [cliniciansData, clinicianEnrolments, isProgramAdministrator, showOnlyMine]);

  // Comprovam si el clínic del llistat administra el programa
  const administersProgram = (program, clinicianId) => {
    return program.accessibleBy.clinicians.find((clinician) => clinician.clinician !== null && clinician.admin === true && clinician.clinician._id === clinicianId); // Si posam primer la condició clinician.admin === true ens estalviam comprovacions, ja que en la majoria de casos .admin serà false
  };

  const clinicianProgramsList = (programsArray, clinicianId) => {
    return (
      <React.Fragment>
        {programsArray.map((program, index) => (
          <Typography key={`cliProLis_${clinicianId}_${index}`} variant='body2'>
            {sessionContext.userId === clinicianId ? (
              <strong>                
                {(administersProgram(program, clinicianId) && (translations[program.sharedId] ? translations[program.sharedId].toUpperCase() : program.name.toUpperCase())) || (translations[program.sharedId] || program.name)}                
              </strong>
            ) : (              
              (administersProgram(program, clinicianId) && (translations[program.sharedId] ? translations[program.sharedId].toUpperCase() : program.name.toUpperCase())) || (translations[program.sharedId] || program.name)
            )}
            <br />
          </Typography>
        ))}
      </React.Fragment>
    );
  };

  const handleCheck = (id, username) => {
    handleSelectedCliniciansData(id, username);
  };

  // const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const searchClinician = (string) => {
    setPage(0);
    setSearchString(string);
    let data = [];
    if (showOnlyMine) {
      data = filteredSearch(onlyMyClinicians(), string, ['username', 'firstName', 'lastName', 'programs']);
    } else {
      data = filteredSearch(cliniciansData, string, ['username', 'firstName', 'lastName', 'programs']);
    }
    const newCliniciansData = filterUserSchedulesStatus(data, timedOut, timedOutDays);
    setFilteredUsers(newCliniciansData);
  };

  // A part d'anar alternant el color de fons de les files,
  // destacam amb un altre color el fons de la fila corresponent a l'usuari clínic que està consultant la web
  const customBgStyle = (index, user) => {
    if (sessionContext.username === user) return classesForm.tableRowMarked;
    if (index % 2) return classesForm.tableRowBgColor1;
    return classesForm.tableRowBgColor2;
  };

  useEffect(() => {
    let data = [];
    if (showOnlyMine) {
      data = filteredSearch(onlyMyClinicians(), searchString, ['username', 'firstName', 'lastName', 'programs']);
    } else {
      data = filteredSearch(cliniciansData, searchString, ['username', 'firstName', 'lastName', 'programs']);
    }
    const newCliniciansData = filterUserSchedulesStatus(data, timedOut, timedOutDays);
    setFilteredUsers(newCliniciansData);
  }, [timedOut, timedOutDays]);

  const cliniciansDataColumns = 4; // Per saber el nombre de propietats de cada objecte

  return (
    <TableContainer component={Paper}>
      <Table aria-label='clinicians list table'>
        <TableHead>
          <TableRow>
            {/* ATENCIÓ: si pintam la columna _id afegir +1 al colSpan */}
            <TableCell colSpan={cliniciansDataColumns + 2} align='left' padding='none'>
              {(selectedClinicians.length > 0 ? (
                <DeleteToolBar numSelected={selectedClinicians.length} handleClick={handleDelete} />
              ) : (
                <Grid container direction='row' alignItems='center' justify='flex-start' spacing={4}>
                  <Grid item>
                    <SearchField handleChange={(string) => searchClinician(string)} value={searchString} />
                  </Grid>
                  <Grid item>
                    <TimedOutShedulesFilter
                      timedOut={timedOut}
                      setTimedOut={setTimedOut}
                      days={timedOutDays}
                      setDays={setTimedOutDays}
                      daysOptions={timedOutDaysOptions}
                    />
                  </Grid>
                  <Grid item>
                    <Grid item>
                      <FormLabel className={classesField.formLabel}>{translations['_onlyMine']}</FormLabel>
                      <FormControlLabel
                        control={(
                          <BlueSwitch
                            checked={!showOnlyMine}
                            onChange={(event) => { setShowOnlyMine(!event.target.checked); }}
                            name='showOnlyMine'
                          />
                        )}
                        label={translations['_everyone']}
                        className={classesField.formControlLabel}
                      />
                    </Grid>
                  </Grid>
                  <Grid item>
                    <IconButton size='medium' onClick={() => getDataSheet(filteredUsers, ['username', 'firstName', 'lastName', 'programs'])}>
                      <Archive />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow className={classesForm.tableRowHeaderBgColor}>
            {/* ATENCIÓ: no pintam la columna _id */}
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'username'}
                direction={orderBy === 'username' ? order : 'asc'}
                onClick={() => handleRequestSort('username')}
              >
                <strong>{translations['_username']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'firstName'}
                direction={orderBy === 'firstName' ? order : 'asc'}
                onClick={() => handleRequestSort('firstName')}
              >
                <strong>{translations['_firstName']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'lastName'}
                direction={orderBy === 'lastName' ? order : 'asc'}
                onClick={() => handleRequestSort('lastName')}
              >
                <strong>{translations['_lastName']}</strong>
              </TableSortLabel>
            </TableCell>
            {/* <TableCell align='left'><strong>{translations['_email']}</strong></TableCell> */}
            <TableCell align='left'><strong>{translations['_programs']}</strong></TableCell>
            <TableCell align='left'><strong>{translations['_forms']}</strong></TableCell>{/* nova columna */}
            <TableCell align='center' />{/* Columna per al botó d'editar */}
            {/* <TableCell align='center'>{sessionContext.role === 'Admin' && <Delete className={classesField.iconBody} />}</TableCell> */}
            <TableCell align='center'><Delete className={classesField.iconBody} /></TableCell>
          </TableRow>
          {(rowsPerPage > 0
            // ATENCIÓ: l'ordenació es fa damunt filteredSchedules, si no s'ha introduit res al camp de cerca el filteredSchedules inclou tot el schedulesData,
            // és a dir, l'ordenació no es fa sobre la "pagina de formularis" que es mostra al llistat (estigui filtrada o no)
            ? stableSort(filteredUsers, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : stableSort(filteredUsers, getComparator(order, orderBy))
          ).map((data, index) => (
            <TableRow key={data._id} className={customBgStyle(index, data.username)} hover>
              {/* <TableRow key={data._id} className={index % 2 ? classesForm.tableRowBgColor1 : classesForm.tableRowBgColor2} hover> */}
              {/* ATENCIÓ: no pintam la columna _id */}
              {/* Només un Admin pot editar/veure la info d'un clínic */}
              {/* Destacam en negreta la fila corresponent a l'usuari clínic que està accedint a la web */}
              <TableCell align='left'>
                {sessionContext.username === data.username ? <strong>{data.username}</strong> : data.username}
              </TableCell>
              <TableCell align='left'>
                {sessionContext.username === data.username ? <strong>{data.firstName}</strong> : data.firstName}
              </TableCell>
              <TableCell align='left'>
                {sessionContext.username === data.username ? <strong>{data.lastName}</strong> : data.lastName}
              </TableCell>
              <TableCell align='left'>
                {/* sessionContext.username === data.username ? <strong>{data.email}</strong> : data.email */}
                {/* Si data.programs és null vol dir que s'ha produit algun error en obtenir els programes al component pare (ListClinicians) */}
                {data.programs !== null ? clinicianProgramsList(data.programs, data._id) : sessionContext.username === data.username ? <strong>{translations['_infoNotAvailable']}</strong> : translations['_infoNotAvailable']}
              </TableCell>
              <TableCell align='left'>
                {data.form !== null && data.form !== undefined
                  && (
                    <IconButton onClick={() => handleViewClinicianSchedules(data._id)}>
                      <Avatar
                          style={
                            data.form > 0
                            ? (data.form > 2 ? { color: '#fff', backgroundColor: greenColor } : { color: '#fff', backgroundColor: amberColor })
                            : { color: '#fff', backgroundColor: redColor }
                          }
                        >
                          { data.form }
                      </Avatar>
                    </IconButton>
                  )}
              </TableCell>
              <TableCell align='center'>
                <IconButton onClick={() => handleViewClinician(data._id)} disabled={!(sessionContext.role === 'Admin' || isProgramAdministrator)}>
                  <Edit />
                </IconButton>
              </TableCell>
              <TableCell align='center'>
                <CustomCheckBox
                  checked={selectedClinicians.findIndex((item) => item.id === data._id) > -1} // Si res.value es dins la taula la condició dona true
                  onClick={() => handleCheck(data._id, data.username)}
                  disabled={sessionContext.role !== 'Admin'} // Només l'administrador pot eliminar clínics
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={cliniciansDataColumns + 2}
              count={filteredUsers.length}
              rowsPerPageOptions={rowsPerPageOptions}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              labelDisplayedRows={({ from, to, count }) => (
                from + ' - ' + ((to !== -1 && to) || count) + ' ' + translations['_of'] + ' ' + ((count !== -1 && count) || (translations['_moreThan'] + ' ') + to)
              )}
              labelRowsPerPage={translations['_rowsPerPage']}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

// Indica i controla els props que se han de passar al component
CliniciansList.propTypes = {
  cliniciansData: PropTypes.array.isRequired,
  clinicianEnrolments: PropTypes.array.isRequired,
  handleViewClinician: PropTypes.func.isRequired,
  handleViewClinicianSchedules: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSelectedCliniciansData: PropTypes.func.isRequired,
  isProgramAdministrator: PropTypes.bool.isRequired,
  selectedClinicians: PropTypes.array.isRequired,
};
